/**
 * Transfers Dual-Write Test
 *
 * Tests that transfer operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * T1: Create single item transfer
 * T2: Create multiple items transfer
 * T3: Delete transfer
 */

import {
  saveNewTransfer,
  deleteTransfer,
} from '../global/Firebase/inventory';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, productsDictAtom, dbAtom } from '../global/AtomsProvider';

// Test variation definitions
export const TRANSFER_TEST_VARIATIONS = [
  { id: 'T1', category: 'create', name: 'Create single item transfer' },
  { id: 'T2', category: 'create', name: 'Create multiple items transfer' },
  { id: 'T3', category: 'delete', name: 'Delete transfer' },
];

// Get test categories for UI grouping
export function getTransferTestCategories() {
  return [
    { id: 'create', name: 'Create Transfers', variations: ['T1', 'T2'] },
    { id: 'delete', name: 'Delete Transfer', variations: ['T3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getTransferTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const db = jotaiStore.get(dbAtom);

  if (!organization || !db) {
    return null;
  }

  // Get stores - need at least 2
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 2) {
    return null; // Need at least 2 stores for transfers
  }

  const fromStore = { id: storeEntries[0][0], ...storeEntries[0][1] };
  const toStore = { id: storeEntries[1][0], ...storeEntries[1][1] };

  // Get first few products for transfer items
  const productEntries = Object.entries(productsDict || {});
  const testProducts = productEntries.slice(0, 3).map(([id, product]) => ({
    id,
    ...product,
    ref: db.collection('products').doc(id),
  }));

  return {
    organization,
    fromStore,
    toStore,
    db,
    organizationId: organization.id,
    testProducts,
  };
}

/**
 * Compare transfer via backend API
 */
async function compareTransferViaApi(transferId, transferData, log) {
  try {
    const response = await api.get(`/transfers/compare/${transferId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        transferId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'transfer',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'amount', sb: 'amount' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(transferData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    // Compare items count
    const fsItemsCount = transferData?.items?.length || 0;
    const sbItemsCount = supabaseData?.items?.length || 0;
    if (fsItemsCount !== sbItemsCount) {
      differences.push({
        field: 'items_count',
        supabaseValue: sbItemsCount,
        firestoreValue: fsItemsCount,
        severity: 'warning',
      });
    }

    return {
      transferId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      transferId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if transfer was deleted from Supabase
 */
async function checkTransferDeleted(transferId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/transfers/compare/${transferId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single transfer test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, fromStore, toStore, organizationId, testProducts } = testData;

  let createdRef = null;
  let createdTransferData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'T1': {
        // Create single item transfer
        const timestamp = Date.now();
        const testProduct = testProducts[0];

        if (!testProduct) {
          result.error = 'No test product available';
          break;
        }

        const items = [{
          id: testProduct.id,
          name: testProduct.name || 'Test Product',
          quantity: 2,
          purchasePrice: testProduct.purchasePrice || 100,
          ref: testProduct.ref,
        }];

        const amount = items.reduce((sum, item) => sum + (item.purchasePrice || 0) * item.quantity, 0);

        const transferData = {
          organizationId,
          fromStore,
          toStore,
          items,
          amount,
          date: new Date(),
          fromDailyStatID: `test-transfer-from-${timestamp}-${fromStore.id}`,
          toDailyStatID: `test-transfer-to-${timestamp}-${toStore.id}`,
        };

        log(`Creating single item transfer: ${testProduct.name}, quantity: 2`);
        createdRef = await saveNewTransfer(db, transferData);
        createdTransferData = { ...transferData, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareTransferViaApi(createdRef.id, transferData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test transfer...');
        await deleteTransfer(db, createdTransferData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'T2': {
        // Create multiple items transfer
        const timestamp = Date.now();

        if (testProducts.length < 2) {
          result.error = 'Need at least 2 products for multiple items test';
          break;
        }

        const items = testProducts.slice(0, 2).map((product, index) => ({
          id: product.id,
          name: product.name || `Test Product ${index + 1}`,
          quantity: 3 + index,
          purchasePrice: product.purchasePrice || 100 + index * 50,
          ref: product.ref,
        }));

        const amount = items.reduce((sum, item) => sum + (item.purchasePrice || 0) * item.quantity, 0);

        const transferData = {
          organizationId,
          fromStore,
          toStore,
          items,
          amount,
          date: new Date(),
          fromDailyStatID: `test-transfer-multi-from-${timestamp}-${fromStore.id}`,
          toDailyStatID: `test-transfer-multi-to-${timestamp}-${toStore.id}`,
        };

        log(`Creating multi-item transfer: ${items.length} items, total amount: ${amount}`);
        createdRef = await saveNewTransfer(db, transferData);
        createdTransferData = { ...transferData, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareTransferViaApi(createdRef.id, transferData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test transfer...');
        await deleteTransfer(db, createdTransferData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'T3': {
        // Delete transfer test
        const timestamp = Date.now();
        const testProduct = testProducts[0];

        if (!testProduct) {
          result.error = 'No test product available';
          break;
        }

        const items = [{
          id: testProduct.id,
          name: testProduct.name || 'Test Product',
          quantity: 1,
          purchasePrice: testProduct.purchasePrice || 50,
          ref: testProduct.ref,
        }];

        const amount = items[0].purchasePrice * items[0].quantity;

        const transferData = {
          organizationId,
          fromStore,
          toStore,
          items,
          amount,
          date: new Date(),
          fromDailyStatID: `test-transfer-del-from-${timestamp}-${fromStore.id}`,
          toDailyStatID: `test-transfer-del-to-${timestamp}-${toStore.id}`,
        };

        log('Creating transfer to delete...');
        createdRef = await saveNewTransfer(db, transferData);
        createdTransferData = { ...transferData, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareTransferViaApi(createdRef.id, transferData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Transfer was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting transfer...');
        await deleteTransfer(db, createdTransferData);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkTransferDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          transferId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdTransferData) {
      try {
        await deleteTransfer(db, createdTransferData);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all transfer tests
 */
export async function runFullTransferTest(log = console.log, selectedVariations = null) {
  log('Starting transfer dual-write tests...');

  const testData = getTransferTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 2 stores and to be logged in.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  if (testData.testProducts.length < 1) {
    log('ERROR: Need at least 1 product to run transfer tests.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('TRANSFERS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`From Store: ${testData.fromStore.name || testData.fromStore.id}`);
  log(`To Store: ${testData.toStore.name || testData.toStore.id}`);
  log(`Test Products: ${testData.testProducts.map(p => p.name || p.id).join(', ')}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || TRANSFER_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = TRANSFER_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = TRANSFER_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  TRANSFER_TEST_VARIATIONS,
  getTransferTestCategories,
  getTransferTestData,
  runFullTransferTest,
};
